import { produce } from 'immer'
import { useCallback, useMemo, useState } from 'react'
import getRootPath from '~utils/app/get-root-path'
import { getValueAsPathFromObject } from '~utils/general'
import { getCapitalizedText } from '~utils/string'

import { Navigate, useNavigate } from '@tanstack/react-router'

import ConsultationLayout from './components/layout'
import SelectCheckboxOptions from './components/select-options/checkbox'
import NumPerDayOptions from './components/select-options/num-per-day'
import SelectPeriodOption from './components/select-options/period'
import SelectOptions from './components/select-options/select'
import { loadedData } from './data'
import { useConsultationScreens } from './hooks/use-consultation-screens'
import {
  getCommonPages,
  getCommonScreenNumber,
  getLegacyPages,
  getLegacyScreenNumber,
} from './screens'
import { useConsultationData } from './store'
import { ConsultationProcess, GroupItem, PeriodDatafields } from './types'

interface CheckupProps {
  main_concern: string
  symptom: string
}
const CheckupPage = ({ main_concern, symptom }: CheckupProps) => {
  const navigate = useNavigate()
  const [consultationData, setConsultationData] = useConsultationData()
  const [nextButtonText, setNextButtonLabel] = useState('')
  const screens = useConsultationScreens(main_concern)

  const currentField = symptom
  const path = useMemo(() => ['checkup', main_concern, symptom].join('.'), [main_concern, symptom])

  const {
    title,
    description,
    component: { options, ...component },
  } = getValueAsPathFromObject<ConsultationProcess>(loadedData, path)
  const rootValue = useMemo(
    () => getValueAsPathFromObject(consultationData, path),
    [consultationData, path]
  )
  // false('No')도 답이다. 비어 있는 값만 미응답으로 본다
  const [nextAvailable, setNextAvailable] = useState(
    Object.values(rootValue).some(
      v => v !== null && v !== undefined && v !== '' && !(Array.isArray(v) && v.length === 0)
    )
  )
  const enabledOptions = useMemo(
    () =>
      Array.isArray(options)
        ? options.filter(
            option =>
              option.enabled === undefined || (consultationData && option.enabled(consultationData))
          )
        : options,
    [consultationData, options]
  )

  /**
   * Todo. 추후에 페이로드를 변경해야함.
   * Component[s] 로 복수 단위의 컴포넌트 입력이 들어올 수 있는 경우, 이 resolveRequired 의 동작 방식을 원래는 변경해야함.
   * 현재는 적합한 페이로드를 구성하기 다소 시간이 걸릴 거 같아서 임시로 처리해둠.
   */
  const setValue = useCallback(
    (newValue: string, datafield?: string, resetBeforeUpdate = false) => {
      setConsultationData(
        produce(_consultationData => {
          // 데이터 필드가 없을 때,
          if (!datafield) {
            const noDataFieldPath = path.split('.').slice(0, -1).join('.')

            getValueAsPathFromObject(_consultationData, noDataFieldPath)[symptom] = newValue
            return _consultationData
          }

          const prevValue = rootValue[datafield]
          if (prevValue !== newValue) {
            if (resetBeforeUpdate) {
              if (Object.keys(rootValue).length > 1) {
                for (const key of Object.keys(rootValue)) {
                  if (datafield !== key) {
                    delete getValueAsPathFromObject(_consultationData, path)[key]
                  }
                }
              }
            }
            getValueAsPathFromObject(_consultationData, path)[datafield] = newValue
            return _consultationData
          }
        })
      )
    },
    [path, rootValue, setConsultationData, symptom]
  )

  const onSelect = useCallback(
    ({
      value: newValue,
      datafield,
      extra_component,
      reset_other_datafields,
      passive,
    }: GroupItem & {
      reset_other_datafields: boolean
      passive?: boolean
    }) => {
      if (component.type === 'select') {
        // 화면이 뜰 때 추가 입력칸이 스스로 초기화하는 호출(passive)은 사용자의 선택이 아니다.
        // 미리 선택을 없앤 뒤(DRT-7112) 이것 때문에 아무것도 안 골라도 Next 가 켜지던 문제를 막는다
        if (!passive) setNextAvailable(!extra_component || !extra_component.mandatory)
        // extra_component 가 있다면 extra_component 의 값을 받아야함.
        setValue(newValue, datafield, reset_other_datafields)
      }
    },
    [component.type, setValue]
  )

  const onCheckboxSelect = useCallback(
    ({
      value: newValue,
      datafield,
      extra_component,
      reset_other_datafields,
    }: GroupItem & {
      reset_other_datafields: boolean
    }) => {
      if (component.type === 'checkbox') {
        setNextButtonLabel('')
        const prevValue = (datafield ? rootValue[datafield] : rootValue) ?? []
        if (Array.isArray(newValue) && (!extra_component || newValue.length < prevValue.length)) {
          if (newValue?.includes('Others') && datafield) {
            setNextAvailable(!!rootValue.others?.length)
          } else if (newValue.length > 0) {
            setNextAvailable(true)
          } else if (!component.required) {
            setNextAvailable(true)
            setNextButtonLabel(getCapitalizedText(`no ${component.subject}`))
          } else {
            setNextAvailable(false)
          }
        } else {
          setNextAvailable(newValue && !extra_component)
        }

        // extra_component 가 있다면 extra_component 의 값을 받아야함.
        setValue(newValue, datafield, reset_other_datafields)
      }
    },
    [component, rootValue, setValue]
  )

  const onPeriodSelect = useCallback(
    ({ value: newValue, datafield }: GroupItem) => {
      if (component.type === 'period') {
        setNextButtonLabel('')
        setNextAvailable(
          Object.values(PeriodDatafields).every(periodDatafield => {
            return datafield === periodDatafield
              ? newValue !== null
              : rootValue?.[periodDatafield] !== null && rootValue?.[periodDatafield] !== undefined
          })
        )
      }
      setValue(newValue, datafield)
    },
    [component.type, rootValue, setValue]
  )

  const onTextAreaChange = useCallback(
    ({ value: newValue, datafield }: GroupItem) => {
      if (component.type === 'num_per_day') {
        setNextAvailable(!!newValue)
      }
      setValue(newValue, datafield)
    },
    [component.type, setValue]
  )

  const goNext = useCallback(() => {
    const currentMainConcernDatafields = consultationData.checkup?.[main_concern]
    if (!currentMainConcernDatafields) {
      throw new Error('No valid concern datafields')
    }

    // 순서: 증상별 화면 → 안전 질문 → 공통 이력(period, vaccine, prevention) → 상세 입력/제출 (DRT-7112)
    // 화면 목록은 data.ts 기준 (예전 임시 답에 남은 삭제된 화면 키를 따라가지 않는다)
    const symptoms = getLegacyPages(main_concern)
    const commonPages = getCommonPages()
    const isCommon = commonPages.includes(symptom)
    const pages = isCommon ? commonPages : symptoms
    const currentSymptomIndex = pages.indexOf(symptom)
    if (currentSymptomIndex === -1) {
      throw new Error('No valid symptom')
    }

    if (pages.length <= currentSymptomIndex + 1) {
      navigate({
        to: isCommon
          ? `${getRootPath()}/ask-a-vet/attachment`
          : `${getRootPath()}/ask-a-vet/safety/${main_concern}`,
        resetScroll: true,
      })
      return
    }

    const nextSymptom = pages[currentSymptomIndex + 1]
    navigate({
      to: `${getRootPath()}/ask-a-vet/preliminary-checkup/${main_concern}/${nextSymptom}/`,
      resetScroll: true,
    })
  }, [consultationData.checkup, main_concern, navigate, symptom])

  const onNextPress = useCallback(() => {
    goNext()
  }, [goNext])

  const onSkipPress = useCallback(() => {
    setConsultationData(
      produce(_consultationData => {
        if (typeof rootValue !== 'object') {
          const noDataFieldPath = path.split('.').slice(0, -1).join('.')
          getValueAsPathFromObject(_consultationData, noDataFieldPath)[currentField] = null
        } else {
          for (const [entryField, entryValue] of Object.entries(rootValue)) {
            if (Array.isArray(entryValue)) {
              getValueAsPathFromObject(_consultationData, path)[entryField] = []
            } else {
              getValueAsPathFromObject(_consultationData, path)[entryField] = null
            }
          }
        }
      })
    )
    goNext()
  }, [currentField, goNext, path, rootValue, setConsultationData])

  /* todo: checkbox 을 위한 지엽적인 페이로드 나중에 이런 코드는 다 없어져야함. */
  // useEffect(() => {
  //   if (!initialized.current) {
  //     initialized.current = true
  //     if (component.type === 'checkbox' && !component.required) {
  //       setNextAvailable(getCapitalizedText(`no ${component.subject}`))
  //     }
  //   }
  // }, [component])

  /**
   * mandatory 쪽 체크 로직 추가
   */
  const isMandatory = useMemo(() => {
    switch (component.type) {
      case 'select':
        return component.mandatory ?? true
      case 'checkbox':
        return component.required ?? false
      case 'period':
      case 'num_per_day':
        return true
      default:
        return true
    }
  }, [component])

  const render = useMemo(() => {
    switch (component.type) {
      case 'select':
        return (
          <SelectOptions
            root={rootValue}
            onSelect={onSelect}
            options={enabledOptions as GroupItem[]}
            {...component}
          />
        )
      case 'checkbox':
        return (
          <SelectCheckboxOptions
            root={rootValue}
            onSelect={onCheckboxSelect}
            options={enabledOptions as GroupItem[]}
            {...component}
          />
        )
      case 'period':
        return (
          <SelectPeriodOption
            root={rootValue}
            onSelect={onPeriodSelect}
            options={enabledOptions as GroupItem[]}
            {...component}
          />
        )
      case 'num_per_day':
        return <NumPerDayOptions setValue={onTextAreaChange} placeholder={component.placeholder} />
      default:
        return null
    }
  }, [component, enabledOptions, onCheckboxSelect, onPeriodSelect, onSelect, rootValue])

  return (
    <ConsultationLayout
      key={path}
      title={title}
      description={description}
      required={isMandatory}
      nextButtonDisabled={!nextAvailable}
      nextButtonText={nextButtonText}
      progress={
        screens && {
          current: screens.commonPages.includes(symptom)
            ? getCommonScreenNumber(screens, symptom)
            : getLegacyScreenNumber(screens, symptom),
          total: screens.total,
        }
      }
      onNextPress={onNextPress}
      onSkipPress={onSkipPress}
    >
      {render}
    </ConsultationLayout>
  )
}

// 삭제된 화면 주소(예전 링크·기록)로 들어오면 증상의 첫 화면(없으면 안전 질문)으로 보낸다
const Checkup = (props: CheckupProps) => {
  const pages = getLegacyPages(props.main_concern)
  if (!pages.includes(props.symptom) && !getCommonPages().includes(props.symptom)) {
    const root = `${getRootPath()}/ask-a-vet`
    const to = !loadedData.checkup[props.main_concern]
      ? `${root}/main-concern`
      : pages.length
        ? `${root}/preliminary-checkup/${props.main_concern}/${pages[0]}/`
        : `${root}/safety/${props.main_concern}`
    return <Navigate to={to} replace />
  }
  return <CheckupPage {...props} />
}

export default Checkup
